import { Client } from './client';

export enum ID_VERIFICATION_STATE {
  INITIALIZED = 'INITIALIZED',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export enum VERIFICATION_FLOW {
  INTERNAL = 'INTERNAL',
  SHUFTI = 'SHUFTI',
  INHERITED = 'INHERITED',
}

export enum DOCUMENT_STATE {
  MISSING = 'MISSING',
  PROVIDED = 'PROVIDED',
  SIMPLE_VALIDATION = 'SIMPLE_VALIDATION',
  CROSS_VALIDATION = 'CROSS_VALIDATION',
  VALIDATED = 'VALIDATED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum PROCESSOR {
  ITF_AUTO = 'ITF_AUTO',
  SHUFTI = 'SHUFTI',
  CLIENT = 'CLIENT',
  OPERATOR = 'OPERATOR',
}

export enum VerificationDecision {
  SUCCESS = 'success',
  FAIL = 'fail',
}

export enum InvalidationReason {
  LOW_QUALITY = 'LOW_QUALITY',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  UNCLEAR_DOCUMENT = 'UNCLEAR_DOCUMENT',
  NO_CARD_DETECTED = 'NO_CARD_DETECTED',
  NO_FACE_DETECTED = 'NO_FACE_DETECTED',
  FACE_OBSTRUCTED = 'FACE_OBSTRUCTED',
  FACE_NOT_MATCH = 'FACE_NOT_MATCH',
  DOCUMENT_PARTIALLY_VISIBLE = 'DOCUMENT_PARTIALLY_VISIBLE',
  MULTIPLE_FACES_DETECTED = 'MULTIPLE_FACES_DETECTED',
  SCREENSHOT_DETECTED = 'SCREENSHOT_DETECTED',
  EDITED_DOCUMENT = 'EDITED_DOCUMENT',
  DATA_MISMATCH = 'DATA_MISMATCH',
  DATA_MISSING = 'DATA_MISSING',
  EGN_MISSING = 'EGN_MISSING',
  EGN_NOT_MATCH = 'EGN_NOT_MATCH',
  DOCUMENT_NUMBER_MISSING = 'DOCUMENT_NUMBER_MISSING',
  DOCUMENT_EXPIRED = 'DOCUMENT_EXPIRED',
  UNDERAGE = 'UNDERAGE',
  FRAUD = 'FRAUD',
  OTHER = 'OTHER',
}

export const OPERATOR_INVALIDATION_REASONS: InvalidationReason[] = [
  InvalidationReason.LOW_QUALITY,
  InvalidationReason.NOT_APPLICABLE,
  InvalidationReason.UNCLEAR_DOCUMENT,
  InvalidationReason.NO_CARD_DETECTED,
  InvalidationReason.NO_FACE_DETECTED,
  InvalidationReason.FACE_OBSTRUCTED,
  InvalidationReason.FACE_NOT_MATCH,
  InvalidationReason.DOCUMENT_PARTIALLY_VISIBLE,
  InvalidationReason.SCREENSHOT_DETECTED,
  InvalidationReason.EDITED_DOCUMENT,
  InvalidationReason.DOCUMENT_NUMBER_MISSING,
  InvalidationReason.EGN_MISSING,
  InvalidationReason.DATA_MISMATCH,
  InvalidationReason.DATA_MISSING,
  InvalidationReason.EGN_NOT_MATCH,
  InvalidationReason.DOCUMENT_EXPIRED,
  InvalidationReason.UNDERAGE,
  InvalidationReason.FRAUD,
  InvalidationReason.OTHER,
];

export interface InvalidationReasonCategory {
  key: string;
  reasons: InvalidationReason[];
}

export const OPERATOR_INVALIDATION_CATEGORIES: InvalidationReasonCategory[] = [
  {
    key: 'quality',
    reasons: [
      InvalidationReason.LOW_QUALITY,
      InvalidationReason.UNCLEAR_DOCUMENT,
      InvalidationReason.DOCUMENT_PARTIALLY_VISIBLE,
      InvalidationReason.NO_CARD_DETECTED,
    ],
  },
  {
    key: 'face',
    reasons: [
      InvalidationReason.NO_FACE_DETECTED,
      InvalidationReason.FACE_OBSTRUCTED,
      InvalidationReason.FACE_NOT_MATCH,
    ],
  },
  {
    key: 'data',
    reasons: [
      InvalidationReason.DOCUMENT_NUMBER_MISSING,
      InvalidationReason.EGN_MISSING,
      InvalidationReason.DATA_MISSING,
      InvalidationReason.DATA_MISMATCH,
      InvalidationReason.EGN_NOT_MATCH,
      InvalidationReason.DOCUMENT_EXPIRED,
      InvalidationReason.UNDERAGE,
    ],
  },
  {
    key: 'fraud',
    reasons: [
      InvalidationReason.SCREENSHOT_DETECTED,
      InvalidationReason.EDITED_DOCUMENT,
      InvalidationReason.FRAUD,
    ],
  },
  {
    key: 'other',
    reasons: [InvalidationReason.NOT_APPLICABLE, InvalidationReason.OTHER],
  },
];

// Visual config (icons, classes, colors)

export const ID_VERIFICATION_STATE_ICON: Record<ID_VERIFICATION_STATE, string> =
{
  [ID_VERIFICATION_STATE.INITIALIZED]: 'play_circle_outline',
  [ID_VERIFICATION_STATE.IN_PROGRESS]: 'hourglass_empty',
  [ID_VERIFICATION_STATE.IN_REVIEW]: 'person',
  [ID_VERIFICATION_STATE.SUCCESS]: 'check_circle',
  [ID_VERIFICATION_STATE.FAIL]: 'cancel',
};

export const ID_VERIFICATION_STATE_CLASS: Record<
  ID_VERIFICATION_STATE,
  string
> = {
  [ID_VERIFICATION_STATE.INITIALIZED]: 'pending',
  [ID_VERIFICATION_STATE.IN_PROGRESS]: 'pending',
  [ID_VERIFICATION_STATE.IN_REVIEW]: 'expired',
  [ID_VERIFICATION_STATE.SUCCESS]: 'approved',
  [ID_VERIFICATION_STATE.FAIL]: 'rejected',
};

export const PROCESSOR_COLOR: Record<string, string> = {
  [PROCESSOR.SHUFTI]: '#6a1b9a',
  [PROCESSOR.OPERATOR]: '#e65100',
  [PROCESSOR.ITF_AUTO]: '#546e7a',
  [PROCESSOR.CLIENT]: '#1565c0',
  SYSTEM: '#90a4ae',
};

export const VERIFICATION_FLOW_LABEL: Record<VERIFICATION_FLOW, string> = {
  [VERIFICATION_FLOW.INTERNAL]: 'Theia (Internal)',
  [VERIFICATION_FLOW.SHUFTI]: 'ShuftiPro (External)',
  [VERIFICATION_FLOW.INHERITED]: 'Inherited',
};

// Document slots

export enum IdCardImageTypes {
  SELFIE = 'SELFIE',
  ID_FRONT = 'ID_FRONT',
  ID_BACK = 'ID_BACK',
}

export type SlotKey = 'selfie' | 'front' | 'back';

export interface SlotConfig {
  apiType: IdCardImageTypes;
  stateKey: 'idFrontState' | 'idBackState' | 'selfieState';
  dtoKey: 'idCardFront' | 'idCardBack' | 'selfie';
  label: string;
  reasonKey?: string;
}

export const SLOT_CONFIGS: Record<SlotKey, SlotConfig> = {
  selfie: {
    apiType: IdCardImageTypes.SELFIE,
    stateKey: 'selfieState',
    dtoKey: 'selfie',
    label: 'Селфи',
  },
  front: {
    apiType: IdCardImageTypes.ID_FRONT,
    stateKey: 'idFrontState',
    dtoKey: 'idCardFront',
    label: 'ЛK — лице',
  },
  back: {
    apiType: IdCardImageTypes.ID_BACK,
    stateKey: 'idBackState',
    dtoKey: 'idCardBack',
    label: 'ЛK— гръб',
  },
};

export interface StartVerificationDialogData {
  client: Client;
  previousVerification: IdentityVerification | null;
  activeVerification: IdentityVerification | null;
}

export interface DocumentOption {
  id: number;
  name: string;
  state: DOCUMENT_STATE;
  stateClass: 'valid' | 'invalid' | 'pending' | 'validating';
  reasonLabel: string;
  isFromPrev: boolean;
}

export interface SlotControl {
  key: SlotKey;
  formControlName: 'frontId' | 'backId' | 'selfieId';
  label: string;
  apiType: IdCardImageTypes;
  options: DocumentOption[];
  hasOptions: boolean;
}

export const SLOT_KEYS: SlotKey[] = ['front', 'back', 'selfie'];

export interface FlowOption {
  value: VERIFICATION_FLOW;
  label: string;
  iconUrl: string;
}

export const FLOW_OPTIONS: FlowOption[] = [
  {
    value: VERIFICATION_FLOW.INTERNAL,
    label: 'Theia',
    iconUrl: '/assets/images/Theia.png',
  },
  {
    value: VERIFICATION_FLOW.SHUFTI,
    label: 'ShuftiPro',
    iconUrl: '/assets/images/ShuftiPro.png',
  },
];

// Document state parsing (state + optional rejection reason)
export interface ParsedDocumentState {
  base: DOCUMENT_STATE;
  reason: InvalidationReason | null;
}

export function parseDocumentState(
  raw: string | null | undefined
): ParsedDocumentState {
  if (!raw) return { base: DOCUMENT_STATE.MISSING, reason: null };

  if (raw.startsWith('REJECTED_')) {
    const reasonStr = raw.substring('REJECTED_'.length);
    const reason = isValidInvalidationReason(reasonStr)
      ? reasonStr
      : InvalidationReason.OTHER;
    return { base: DOCUMENT_STATE.REJECTED, reason };
  }

  if (raw === 'REJECTED') {
    return { base: DOCUMENT_STATE.REJECTED, reason: InvalidationReason.OTHER };
  }

  return { base: raw as DOCUMENT_STATE, reason: null };
}

export function isValidInvalidationReason(
  value: string
): value is InvalidationReason {
  return Object.values(InvalidationReason).includes(
    value as InvalidationReason
  );
}

// Document state visual config

export const DOCUMENT_STATE_CLASS: Record<
  DOCUMENT_STATE,
  'valid' | 'invalid' | 'pending' | 'validating' | 'accepted'
> = {
  [DOCUMENT_STATE.MISSING]: 'pending',
  [DOCUMENT_STATE.PROVIDED]: 'validating',
  [DOCUMENT_STATE.SIMPLE_VALIDATION]: 'validating',
  [DOCUMENT_STATE.CROSS_VALIDATION]: 'validating',
  [DOCUMENT_STATE.VALIDATED]: 'valid',
  [DOCUMENT_STATE.ACCEPTED]: 'accepted',
  [DOCUMENT_STATE.REJECTED]: 'invalid',
};

export const DOCUMENT_STATE_ICON: Record<DOCUMENT_STATE, string> = {
  [DOCUMENT_STATE.MISSING]: 'image_not_supported',
  [DOCUMENT_STATE.PROVIDED]: 'hourglass_empty',
  [DOCUMENT_STATE.SIMPLE_VALIDATION]: 'hourglass_empty',
  [DOCUMENT_STATE.CROSS_VALIDATION]: 'hourglass_empty',
  [DOCUMENT_STATE.VALIDATED]: 'check_circle',
  [DOCUMENT_STATE.ACCEPTED]: 'check_circle',
  [DOCUMENT_STATE.REJECTED]: 'cancel',
};

export const IN_PROGRESS_STATES: DOCUMENT_STATE[] = [
  DOCUMENT_STATE.PROVIDED,
  DOCUMENT_STATE.SIMPLE_VALIDATION,
  DOCUMENT_STATE.CROSS_VALIDATION,
];

// DTO types

export interface CreateVerificationDto {
  civilId: string;
  brand: string;
  flow: VERIFICATION_FLOW;
  documents?: {
    idFrontId?: number;
    idBackId?: number;
    selfieId?: number;
  };
}

export interface IdentityVerification {
  id: number;
  state: ID_VERIFICATION_STATE;
  flow: VERIFICATION_FLOW;
  startedAt: string;
  endedAt: string | null;

  civilId: string;
  brand: string;

  idCardFront: number | null;
  idFrontState: DOCUMENT_STATE | string | null;
  idCardFrontBack: number | null;
  idBackState: DOCUMENT_STATE | string | null;
  selfie: number | null;
  selfieState: DOCUMENT_STATE | string | null;

  miReportId: number | null;
  videoFileId: string | null;
  workflowId: string | null;

  redirectionUrl: string | null;

  events?: IdentityVerificationEvent[];
}

export interface IdentityVerificationEvent {
  id: number;
  type: eventType;
  processor: string;
  reference: string | null;
  createdAt: string;
  data: Record<string, unknown> | null;
}

export enum eventType {
  "VERIFICATION_INITIALIZED" = 'VERIFICATION_INITIALIZED',
  "SHUFTI_ONSITE_INITIALIZED" = 'SHUFTI_ONSITE_INITIALIZED',
  "SHUFTI_ONSITE_ENDED" = 'SHUFTI_ONSITE_ENDED',
  "SHUFTI_ONSITE_PARSED" = 'SHUFTI_ONSITE_PARSED',
  "VERIFICATION_IN_PROGRESS" = 'VERIFICATION_IN_PROGRESS',
  "VERIFICATION_IN_REVIEW" = 'VERIFICATION_IN_REVIEW',
  "VERIFICATION_FAIL" = 'VERIFICATION_FAIL',
  "VERIFICATION_SUCCESS" = 'VERIFICATION_SUCCESS',
  "MI_REPORT_PROVISIONED" = 'MI_REPORT_PROVISIONED',
  "THEIA_CROSS_VALIDATION_ERROR" = 'THEIA_CROSS_VALIDATION_ERROR',
  "SHUFTI_ONSITE_CALLBACK" = 'SHUFTI_ONSITE_CALLBACK',
  "REJECTED" = 'REJECTED',
  "PROVIDED" = 'PROVIDED',
  "VALIDATED" = 'VALIDATED',
  "ACCEPTED" = 'ACCEPTED',
  "SIMPLE_VALIDATION" = 'SIMPLE_VALIDATION',
  "CROSS_VALIDATION" = 'CROSS_VALIDATION',
  "DOCUMENT_PROVISIONED" = 'DOCUMENT_PROVISIONED',
  "VERIFICATION_INHERITED" = 'VERIFICATION_INHERITED'
}

export { ID_VERIFICATION_STATE as ID_VERIFICATION_STATUS };
export { DOCUMENT_STATE as DOCUMENT_STATUS };
export { parseDocumentState as parseDocumentStatus };
